// src/hooks/useVisualizationData.js
import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { FinanceContext } from '../store/financeContext';
import { MarketingContext } from '../store/marketingContext';
import { ProductContext } from '../store/productContext';
import { TeamContext } from '../store/teamContext';
import { detectAnomalies, normalizeData, groupAndAggregate, generateNLInsights } from '../utils/dataUtils';

/**
 * Hook personalizado para preparar, processar e fornecer dados para visualizações
 * 
 * @param {Object} options Opções de configuração
 * @param {string} options.dataType Tipo de dados ('finance', 'marketing', 'products', 'team')
 * @param {string} options.timeRange Intervalo de tempo ('7d', '30d', '90d', 'year', 'all')
 * @param {string} options.groupBy Campo para agrupar dados
 * @param {Array} options.metrics Lista de métricas a calcular
 * @param {Object} options.filters Filtros a aplicar aos dados
 * @param {boolean} options.detectAnomalies Detectar anomalias nos dados
 * @param {boolean} options.generateInsights Gerar insights automáticos
 * @param {Function} options.dataTransform Função personalizada de transformação
 * @returns {Object} Dados processados, status de carregamento e funções auxiliares
 */
function useVisualizationData(options = {}) {
  const {
    dataType = 'finance',
    timeRange = '30d',
    groupBy = null,
    metrics = [],
    filters = {},
    detectAnomalies: shouldDetectAnomalies = false,
    generateInsights: shouldGenerateInsights = false,
    dataTransform = null
  } = options;

  // Estado para dados processados
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState([]);
  const [anomalies, setAnomalies] = useState([]);

  // Acessar contextos relevantes para o tipo de dados
  const financeContext = useContext(FinanceContext);
  const marketingContext = useContext(MarketingContext);
  const productContext = useContext(ProductContext);
  const teamContext = useContext(TeamContext);

  // Função para obter dados do contexto apropriado
  const getDataFromContext = useCallback(() => {
    switch (dataType) {
      case 'finance':
        return {
          rawData: financeContext?.transactions || [],
          previousData: financeContext?.previousTransactions || [],
          targets: financeContext?.targets || {}
        };
      case 'marketing':
        return {
          rawData: marketingContext?.campaigns || [],
          previousData: marketingContext?.previousCampaigns || [],
          channelData: marketingContext?.channelMetrics || {},
          targets: marketingContext?.targets || {}
        };
      case 'products':
        return {
          rawData: productContext?.products || [],
          previousData: productContext?.previousProducts || [],
          salesData: productContext?.salesData || [],
          targets: productContext?.targets || {}
        };
      case 'team':
        return {
          rawData: teamContext?.members || [],
          previousData: teamContext?.previousMembers || [],
          targets: teamContext?.targets || {}
        };
      default:
        console.warn(`Tipo de dados desconhecido: ${dataType}`);
        return { rawData: [], previousData: [], targets: {} };
    }
  }, [dataType, financeContext, marketingContext, productContext, teamContext]);

  // Aplicar filtro de intervalo de tempo
  const applyTimeFilter = useCallback((rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    if (timeRange === 'all') return [...rawData];

    const now = new Date();
    let startDate;

    switch (timeRange) {
      case '7d':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case '30d':
        startDate = new Date(now.setDate(now.getDate() - 30));
        break;
      case '90d':
        startDate = new Date(now.setDate(now.getDate() - 90));
        break;
      case 'year':
        startDate = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
      default:
        startDate = new Date(now.setDate(now.getDate() - 30)); // Default to 30 days
    }

    return rawData.filter(item => {
      // Verificar diferentes campos de data possíveis
      const dateField = item.date || item.createdAt || item.timestamp;
      if (!dateField) return true; // Se não tiver data, incluir por padrão

      const itemDate = new Date(dateField);
      return itemDate >= startDate;
    });
  }, [timeRange]);

  // Aplicar filtros customizados
  const applyFilters = useCallback((data) => {
    if (!data || !Array.isArray(data) || Object.keys(filters).length === 0) {
      return data;
    }

    return data.filter(item => {
      // Verificar todos os filtros
      return Object.keys(filters).every(key => {
        const filterValue = filters[key];
        const itemValue = item[key];

        // Pular filtros com valor vazio
        if (filterValue === undefined || filterValue === null || filterValue === '') {
          return true;
        }

        // Filtro de array (IN)
        if (Array.isArray(filterValue)) {
          return filterValue.includes(itemValue);
        }

        // Filtro de objeto (range)
        if (typeof filterValue === 'object' && filterValue !== null) {
          const { min, max } = filterValue;
          if (min !== undefined && max !== undefined) {
            return itemValue >= min && itemValue <= max;
          } else if (min !== undefined) {
            return itemValue >= min;
          } else if (max !== undefined) {
            return itemValue <= max;
          }
          return true;
        }

        // Filtro de string (contains)
        if (typeof itemValue === 'string' && typeof filterValue === 'string') {
          return itemValue.toLowerCase().includes(filterValue.toLowerCase());
        }

        // Filtro de igualdade
        return itemValue === filterValue;
      });
    });
  }, [filters]);

  // Agrupar dados se necessário
  const groupData = useCallback((filteredData) => {
    if (!filteredData || !Array.isArray(filteredData) || !groupBy) {
      return filteredData;
    }

    // Configuração de agregações com base nas métricas
    const aggregations = {};
    metrics.forEach(metric => {
      aggregations[metric.key] = {
        type: metric.aggregation || 'sum',
        field: metric.field || metric.key
      };
    });

    return groupAndAggregate(filteredData, groupBy, aggregations, true);
  }, [groupBy, metrics]);

  // Detectar anomalias em métricas específicas
  const findAnomalies = useCallback((processedData) => {
    if (!processedData || !Array.isArray(processedData) || !shouldDetectAnomalies) {
      return { data: processedData, anomalies: [] };
    }

    const anomalyMetrics = metrics.filter(m => m.detectAnomalies);
    if (anomalyMetrics.length === 0) return { data: processedData, anomalies: [] };

    let detectedAnomalies = [];
    let dataWithAnomalyFlags = [...processedData];

    anomalyMetrics.forEach(metric => {
      const threshold = metric.anomalyThreshold || 2.0;
      const result = detectAnomalies(dataWithAnomalyFlags, metric.key, threshold);
      
      // Capturar anomalias
      const metricAnomalies = result
        .filter(item => item.isAnomaly)
        .map(item => ({
          ...item,
          metricKey: metric.key,
          metricLabel: metric.label || metric.key
        }));
      
      detectedAnomalies = [...detectedAnomalies, ...metricAnomalies];
      dataWithAnomalyFlags = result;
    });

    return {
      data: dataWithAnomalyFlags,
      anomalies: detectedAnomalies
    };
  }, [metrics, shouldDetectAnomalies]);

  // Gerar insights com base nos dados
  const createInsights = useCallback((processedData, previousData, targets) => {
    if (!processedData || !shouldGenerateInsights) {
      return [];
    }

    // Preparar objetos de métricas para a geração de insights
    const metricObjects = {};
    
    metrics.forEach(metric => {
      // Calcular valor atual
      let currentValue = 0;
      if (metric.aggregation === 'avg' || metric.aggregation === 'average') {
        const values = processedData
          .map(item => parseFloat(item[metric.key]))
          .filter(val => !isNaN(val));
        currentValue = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      } else {
        currentValue = processedData.reduce((sum, item) => {
          const value = parseFloat(item[metric.key]);
          return sum + (isNaN(value) ? 0 : value);
        }, 0);
      }

      // Calcular valor anterior para comparação
      let previousValue = 0;
      if (previousData && previousData.length) {
        if (metric.aggregation === 'avg' || metric.aggregation === 'average') {
          const values = previousData
            .map(item => parseFloat(item[metric.key]))
            .filter(val => !isNaN(val));
          previousValue = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
        } else {
          previousValue = previousData.reduce((sum, item) => {
            const value = parseFloat(item[metric.key]);
            return sum + (isNaN(value) ? 0 : value);
          }, 0);
        }
      }

      // Adicionar ao objeto de métricas
      metricObjects[metric.key] = {
        key: metric.key,
        label: metric.label || metric.key,
        value: currentValue,
        previousPeriod: previousValue,
        change: previousValue ? ((currentValue - previousValue) / previousValue) * 100 : null,
        target: targets ? targets[metric.key] : undefined,
        higherIsBetter: metric.higherIsBetter !== false
      };
    });

    // Gerar insights
    const generatedInsights = generateNLInsights(metricObjects);
    return generatedInsights;
  }, [metrics, shouldGenerateInsights]);

  // Efeito principal para processar dados
  useEffect(() => {
    const processData = async () => {
      try {
        setLoading(true);
        
        // Obter dados dos contextos
        const { rawData, previousData, targets } = getDataFromContext();
        
        if (!rawData || !Array.isArray(rawData)) {
          throw new Error(`Dados inválidos para processamento: ${dataType}`);
        }
        
        // Aplicar filtro de tempo
        const timeFilteredData = applyTimeFilter(rawData);
        const timeFilteredPrevious = previousData ? applyTimeFilter(previousData) : [];
        
        // Aplicar filtros customizados
        const filteredData = applyFilters(timeFilteredData);
        const filteredPrevious = applyFilters(timeFilteredPrevious);
        
        // Agrupar dados se necessário
        const groupedData = groupData(filteredData);
        const groupedPrevious = groupData(filteredPrevious);
        
        // Transformação personalizada (se fornecida)
        let processedData = groupedData;
        if (dataTransform && typeof dataTransform === 'function') {
          processedData = dataTransform(processedData);
        }
        
        // Detectar anomalias
        const { data: dataWithAnomalies, anomalies: detectedAnomalies } = 
          findAnomalies(processedData);
        
        // Gerar insights
        const generatedInsights = createInsights(
          dataWithAnomalies, 
          groupedPrevious, 
          targets
        );
        
        // Atualizar estado
        setData(dataWithAnomalies);
        setAnomalies(detectedAnomalies);
        setInsights(generatedInsights);
        setError(null);
      } catch (err) {
        console.error('Erro ao processar dados de visualização:', err);
        setError(err.message || 'Erro ao processar dados');
      } finally {
        setLoading(false);
      }
    };
    
    processData();
  }, [
    dataType, 
    timeRange, 
    groupBy, 
    metrics, 
    filters, 
    dataTransform,
    getDataFromContext,
    applyTimeFilter,
    applyFilters,
    groupData,
    findAnomalies,
    createInsights
  ]);

  // Funções auxiliares expostas pelo hook
  const refresh = useCallback(() => {
    setLoading(true);
    // A lógica de processamento será acionada pelo useEffect
  }, []);

  const exportData = useCallback((format = 'csv') => {
    if (!data || !data.length) return '';
    
    if (format === 'csv') {
      // Implementação simplificada, você pode expandir conforme necessário
      const headers = Object.keys(data[0]).join(',');
      const rows = data.map(row => 
        Object.values(row).map(val => 
          typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val
        ).join(',')
      );
      return [headers, ...rows].join('\n');
    }
    
    if (format === 'json') {
      return JSON.stringify(data, null, 2);
    }
    
    return '';
  }, [data]);

  // Calcular resumos e agregações para os dados processados
  const summary = useMemo(() => {
    if (!data || !data.length) return {};
    
    const result = {};
    
    metrics.forEach(metric => {
      const values = data
        .map(item => parseFloat(item[metric.key]))
        .filter(val => !isNaN(val));
      
      if (!values.length) {
        result[metric.key] = { value: 0, min: 0, max: 0, avg: 0 };
        return;
      }
      
      const sum = values.reduce((a, b) => a + b, 0);
      const avg = sum / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);
      
      result[metric.key] = {
        value: metric.aggregation === 'avg' ? avg : sum,
        min,
        max,
        avg
      };
    });
    
    return result;
  }, [data, metrics]);

  return {
    data,
    loading,
    error,
    insights,
    anomalies,
    summary,
    refresh,
    exportData
  };
}

export default useVisualizationData;
